import type { FC } from 'react'

import { Text, ImageIcon } from 'components'
import { List } from 'components/common/molecules/list'
import { STATIC_FILES_CDN_URL } from 'constant'

import { FeatureWrapper, FeatureItem, FeatureData, FeatureIcon } from './styled'

export enum FeatureTypes {
  SMOKING,
  INSURANCE,
  PETS,
  PARKING,
}

const FEATURES_ICON = {
  [FeatureTypes.SMOKING]: `${STATIC_FILES_CDN_URL}smoking.svg`,
  [FeatureTypes.INSURANCE]: `${STATIC_FILES_CDN_URL}insurance.svg`,
  [FeatureTypes.PETS]: `${STATIC_FILES_CDN_URL}pets.svg`,
  [FeatureTypes.PARKING]: `${STATIC_FILES_CDN_URL}parking.svg`,
}

export type Feature = {
  type: FeatureTypes
  title: string
  description: string
  order: number
}

export interface FeaturesProps {
  items: Features
}
export const prepareItemForFeatures = (features: Features) => {
  const items = Object.keys(features)
    .map((featureType) => {
      switch (featureType) {
        case 'hasDedicatedParkingSpot': {
          return {
            type: FeatureTypes.PARKING,
            title: 'Dedicated Parking',
            description: features.hasDedicatedParkingSpot ? 'Yes' : 'No',
            order: 4,
          }
        }
        case 'isSmokingAllowed': {
          return {
            type: FeatureTypes.SMOKING,
            title: 'Smoking',
            description: features.isSmokingAllowed ? 'Yes' : 'No',
            order: 1,
          }
        }
        case 'isPetAllowed': {
          const description = features.isPetAllowed
            ? `Yes ${features.allowedPets ?? (features.allowedPets || []).join(', ')}`
            : 'No'
          return {
            type: FeatureTypes.PETS,
            title: 'Pets',
            description,
            order: 3,
          }
        }
        case 'isInsuranceRequired': {
          return {
            type: FeatureTypes.INSURANCE,
            title: 'Renters Insurance',
            description: features.isInsuranceRequired ? 'Required' : 'No',
            order: 2,
          }
        }
        default: {
          return undefined
        }
      }
    })
    .filter((item) => !!item) as Feature[]
  return items.sort((a, b) => a.order - b.order)
}

export const Features: FC<FeaturesProps> = (props: FeaturesProps) => {
  const { items = { hasDedicatedParkingSpot: false } } = props
  const featureItems = prepareItemForFeatures(items)
  return (
    <FeatureWrapper data-testid="features">
      <List
        dataSource={featureItems}
        renderItem={(item) => (
          <FeatureItem key={item.type} data-testid="feature">
            <FeatureIcon>
              <ImageIcon src={FEATURES_ICON[item.type]} />
            </FeatureIcon>
            <FeatureData>
              <Text $bold>{item.title}</Text>
              <Text>{item.description}</Text>
            </FeatureData>
          </FeatureItem>
        )}
      />
    </FeatureWrapper>
  )
}
